import { useMemo } from 'react'
import {Provider} from 'react-redux' // redux
import store from './redux/store/store' // Redux
import { BrowserRouter as Router } from 'react-router-dom'
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as muiLocales from '@mui/material/locale';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import Content from './components/content/Content';
import AccessibilityStatement from './components/accessibility/AccessibilityStatement';
import { I18nProvider, useI18n, langMeta } from './i18n';
/* ★ הלוגו **שלנו** בפינת הדסקטופ — הגרסה הקומפקטית.
   ⚠️ ב-02/08 הוחלף לגרסה הרחבה עם ה-tagline (`anstech-logo-full.png`) והוחזר
      ב-04/08 לבקשת-המשתמש ("היה יותר יפה"). הקומפקטי מרונדר 200×75 ונקי;
      הרחב חייב ~340px כדי שה-tagline יהיה קריא, ובגודל הזה הוא כבד מדי לפינה.
   ★ אותו קובץ משמש גם כלוגו ברירת-המחדל של לקוח ללא `site_logo`
     (‏`tenants.js`) — ולכן אין לדרוס אותו, רק להצביע אליו. */
import ansLogo from './assets/images/ans-logo.png';
import handPhoneImg from './assets/images/handPhone-cut.png';
import { APP_VERSION } from './config/version';
import './App.css'

/* ══════════════════════════════════════════════════════════════════════════
   F1 — שני מטמוני-emotion **קבועים**, אחד לכל כיווניות.

   ⚠️⚠️ למה לא ליצור מטמון חדש בכל החלפת-שפה: ‏emotion מזריק את ה-CSS לראש
      המסמך וזוכר מה כבר הוזרק. מטמון חדש עם אותו `key` היה מזריק שוב את אותם
      כללים תחת אותו שם-מחלקה, ומטמון שנזרק לאשפה משאיר את ה-`<style>` שלו
      במסמך. שני מטמונים יציבים עם **מפתחות שונים** (`muirtl`/`muiltr`)
      מייצרים שני מרחבי-שמות נפרדים, וההחלפה ביניהם היא רק החלפת-ספק.
   ★ ה-`key` הוא גם קידומת שם-המחלקה, ולכן חייב להיות שונה בין השניים —
     אחרת הכללים ההפוכים היו דורסים זה את זה.
   ══════════════════════════════════════════════════════════════════════════ */
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const cacheLtr = createCache({ key: 'muiltr', stylisPlugins: [prefixer] });

function ThemedApp() {
  const { lang, dir } = useI18n();

  const theme = useMemo(() => createTheme({
    // ★ נגזר מהשפה ולא קבוע 'rtl'. ‏MUI משתמש בזה כדי להחליט לאיזה צד
    //   נפתחים תפריטים, לאן זזים המחוונים, ואיך מסודרים אייקוני-הכפתורים.
    direction: dir,
    palette: {
      primary: {
        main: '#15497c',
      },
      secondary: {
        main: '#5b6b7f',
      },
      background: {
        default: '#f4f6f9',
      },
      text: {
        primary: '#10204d',
      },
    },
    typography: {
      /* ★ Heebo מכסה עברית+לטינית אך **לא** ערבית וקירילית. הגופנים
         הבאים ברשימה הם ברירות-המחדל של מערכת-ההפעלה, שמכסות את שתיהן,
         ולכן ערבית/רוסית נופלות אליהן מעצמן במקום להיות מוצגות בגליפי-
         fallback מכוערים. */
      fontFamily: '"Heebo", "Segoe UI", system-ui, -apple-system, Roboto, sans-serif',
      fontSize: 16,
      button: {
        fontWeight: 700,
        textTransform: 'none',
      },
    },
    shape: {
      // Moderated from an extreme 50px. A single value keeps inputs, buttons and
      // surfaces visually consistent.
      borderRadius: 12,
    },
    components: {
      // NOTE: v5 uses `components.styleOverrides` (the old `overrides` key was
      // silently ignored, which is why button styling was inconsistent before).
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
    },
  },
  // תרגומי-הרכיבים של MUI עצמו (aria-labels של הפיג'ר, הלוח וכו')
  muiLocales[langMeta(lang).muiLocale] || muiLocales.heIL,
  ), [lang, dir]);

  return (
    <CacheProvider value={dir === 'rtl' ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <a
          href="https://www.anstc.com"
          target="_blank"
          rel="noopener noreferrer"
          className="ans-corner-logo"
          aria-label="ANS Tech"
        >
          <img src={ansLogo} alt="ANS Tech" />
        </a>
        <img
          src={handPhoneImg}
          className="hand-phone-bg"
          alt=""
          aria-hidden="true"
        />
        {/* ★ הכיווניות נגזרת מהשפה. עד F1 היה כאן `dir="rtl"` קשיח, שהיה
            כופה RTL על רוסית ואנגלית גם אחרי שה-`<html>` כבר עבר ל-LTR. */}
        <div dir={dir} className='phone_container'>
          <Content />
        </div>
        {/* desktop-only footer — rendered OUTSIDE the phone frame (the frame's
            transform+overflow would trap/clip it), fixed to the page's bottom-left.
            Hidden on mobile (the in-phone a11y link is used there instead). The a11y
            link shows on EVERY desktop screen (showAlways); the mobile one is login
            only. Copyright + version mirror the dashboard footer. */}
        <div className="ans-desktop-footer">
          <AccessibilityStatement className="ans-a11y-trigger ans-a11y-trigger--desktop" showAlways />
          <div className="ans-copyright">
            Copyright © ANS Tech ltd.<br />
            All rights reserved.<br />
            v{APP_VERSION}
          </div>
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
}

function App() {
  return (
    <Router>
      <Provider store={store}>
        {/* ★ ה-provider עוטף את התמה ולא להפך: התמה **נגזרת** מהשפה
            (‏direction + locale של MUI), ולכן היא חייבת להיות מתחתיו. */}
        <I18nProvider>
          <ThemedApp />
        </I18nProvider>
      </Provider>
    </Router>
  )
}

export default App
